import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, Enums, inject, NothingHere, TasksStore } from 'Mri.Web.Common';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Input } from 'reactstrap';
import { AppStore } from '../../../Core/AppStore';
import { DisplaysStore } from '../../DisplaysStore';
import { PowerOptionDropdown } from './PowerOptionDropdown';

export interface IDisplayControlSettingsViewParams {
    readonly serialNumber: string;
}

export interface IDeviceControlSettingsProps extends RouteComponentProps<IDisplayControlSettingsViewParams> {}

@autoinject
@observer
export class DeviceControlSettings extends React.Component<IDeviceControlSettingsProps> {
    public render() {
        const serialNumber = this.props.match.params.serialNumber;

        return (
            <div className="container-fluid px-2 px-md-4">
                <div className="row">
                    <div className="col-md-4 py-2">
                        <h5>Display</h5>
                    </div>
                    <div className="col-md-8">
                        <div className="container-fluid">
                            {this.displaySettings && (
                                <div className="row py-2">
                                    <div className="col-3 p-0">
                                        <strong>Power</strong>
                                    </div>
                                    <div className="col-9 p-0 text-end">
                                        <PowerOptionDropdown sendActionAsync={this.sendDisplaySettingsAsync} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="col-md-4 py-2 border-top">
                        <h5>Aux Devices</h5>
                    </div>
                    <div className="col-md-8 border-top">
                        <div className="container-fluid">
                            {this.displaySettings?.auxDevices?.map(a => (
                                <div className="row py-2" key={a.deviceId}>
                                    <div className="col-3 p-0">
                                        <strong>{Enums.toString(Enums.ConnectedDevice, a.deviceId)}</strong>
                                    </div>
                                    <div className="col-9 p-0 text-end">
                                        <PowerOptionDropdown
                                            sendActionAsync={p => this.sendAuxDisplaySettingsAsync(p, a)}
                                        />
                                    </div>
                                </div>
                            ))}
                            {!this.displaySettings?.auxDevices?.length && (
                                <div className="row py-2">
                                    <div className="col-3 p-0">
                                        <Input type="select" value={this.auxDevice} onChange={this.onAuxDeviceChange}>
                                            <option value="" disabled>
                                                Choose an Aux Device
                                            </option>
                                            <option value={Mri.DisplayApi.ConnectedDevice.deviceModem}>
                                                {Enums.toString(
                                                    Enums.ConnectedDevice,
                                                    Mri.DisplayApi.ConnectedDevice.deviceModem
                                                )}
                                            </option>
                                            <option value={Mri.DisplayApi.ConnectedDevice.deviceRouter}>
                                                {Enums.toString(
                                                    Enums.ConnectedDevice,
                                                    Mri.DisplayApi.ConnectedDevice.deviceRouter
                                                )}
                                            </option>
                                            <option value={Mri.DisplayApi.ConnectedDevice.devicePlayer1}>
                                                {Enums.toString(
                                                    Enums.ConnectedDevice,
                                                    Mri.DisplayApi.ConnectedDevice.devicePlayer1
                                                )}
                                            </option>
                                            <option value={Mri.DisplayApi.ConnectedDevice.devicePlayer2}>
                                                {Enums.toString(
                                                    Enums.ConnectedDevice,
                                                    Mri.DisplayApi.ConnectedDevice.devicePlayer2
                                                )}
                                            </option>
                                            <option value={Mri.DisplayApi.ConnectedDevice.deviceM2M}>
                                                {Enums.toString(
                                                    Enums.ConnectedDevice,
                                                    Mri.DisplayApi.ConnectedDevice.deviceM2M
                                                )}
                                            </option>
                                            <option value={Mri.DisplayApi.ConnectedDevice.sigmaController1}>
                                                {Enums.toString(
                                                    Enums.ConnectedDevice,
                                                    Mri.DisplayApi.ConnectedDevice.sigmaController1
                                                )}
                                            </option>
                                            <option value={Mri.DisplayApi.ConnectedDevice.sigmaController2}>
                                                {Enums.toString(
                                                    Enums.ConnectedDevice,
                                                    Mri.DisplayApi.ConnectedDevice.sigmaController2
                                                )}
                                            </option>
                                        </Input>
                                    </div>
                                    <div className="col-9 p-0 text-end">
                                        {typeof this.auxDevice == 'number' && (
                                            <PowerOptionDropdown
                                                sendActionAsync={p =>
                                                    this.sendAuxDisplaySettingsAsync(p, {
                                                        deviceId: this.auxDevice as Mri.DisplayApi.ConnectedDevice,
                                                        powerStatus: p,
                                                    })
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                            <NothingHere
                                empty={!this.displaySettings || !this.displaySettings.auxDevices}
                                loading={false}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    @bind
    private async sendDisplaySettingsAsync(powerAction: Mri.DisplaySettings.PowerStatus) {
        if (this.displaySettings) {
            this.displaySettings.display.powerStatus = powerAction;
            const task = this.tasksStore.startSessionTask(`Send Display Settings`);

            try {
                await this.displaysStore.sendDisplaySettingsAsync(
                    this.props.match.params.serialNumber,
                    this.displaySettings.display
                );
                task.complete();
            } catch (e: any) {
                task.error(e);
            }
        }
    }

    @bind
    private async sendAuxDisplaySettingsAsync(
        powerAction: Mri.DisplaySettings.PowerStatus,
        device: Mri.DisplaySettings.AuxDevice
    ) {
        if (this.displaySettings) {
            device.powerStatus = powerAction;
            const task = this.tasksStore.startSessionTask(`Send Aux Settings`);

            try {
                await this.displaysStore.sendAuxDisplaySettingsAsync(this.props.match.params.serialNumber, device);
                task.complete();
            } catch (e: any) {
                task.error(e);
            }
        }
    }

    @bind
    @action
    private onAuxDeviceChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;

        const value = Number(target.value);
        if (!isNaN(value)) {
            this.auxDevice = value;
        } else {
            this.auxDevice = '';
        }
    }

    @computed
    private get displaySettings() {
        return this.displaysStore.displaySettings.get(this.props.match.params.serialNumber);
    }

    @observable
    private auxDevice: Mri.DisplayApi.ConnectedDevice.devicePlayer1 | '' = '';
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private tasksStore!: TasksStore;
}
